import React, { useMemo, useCallback } from "react";
import type {
  DyadMatrixBlock,
  DyadMatrixEntry,
} from "@unruly-global/api-types";
import { DYAD_STATUS_PALETTE } from "./dyadConstants";

import styles from "./DyadMatrixRenderer.module.css";

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

function statusStyle(score: number) {
  const p = DYAD_STATUS_PALETTE[score];
  return p ? { bg: p.bg, text: p.text } : { bg: "#e5e7eb", text: "#666" };
}

// ---------------------------------------------------------------------------
// Country → flag image URL (flagcdn.com)
// ---------------------------------------------------------------------------

/** Returns a flag URL for 2-char ISO Alpha-2 country IDs.
 *  Composite entities (e.g. "UKEU", "EU") return null — no flag shown. */
function flagUrl(countryId?: string): string | null {
  if (!countryId || countryId.length !== 2) return null;
  return `https://flagcdn.com/w40/${countryId.toLowerCase()}.png`;
}

// ---------------------------------------------------------------------------
// Find the block uid for a dyad's qualitative analysis
// ---------------------------------------------------------------------------

function dyadTitleVariants(a: string, b: string): string[] {
  return [
    `${a} – ${b}`,
    `${b} – ${a}`,
    `${a} - ${b}`,
    `${b} - ${a}`,
    `${a} — ${b}`,
    `${b} — ${a}`,
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CountryLabel: React.FC<{ name: string; countryId?: string }> = ({
  name,
  countryId,
}) => {
  const src = flagUrl(countryId);
  return (
    <div className={styles.countryRow}>
      {src ? (
        <img src={src} alt={name} className={styles.flag} />
      ) : (
        <span className={styles.flagFallback}>●</span>
      )}
      <span className={styles.countryName}>{name}</span>
    </div>
  );
};

interface DyadMatrixRendererProps {
  block: DyadMatrixBlock;
  activeFilters?: Set<string>;
  countryFilters?: Set<string>;
  dyadBlockMap?: Map<string, string>;
}

export const DyadMatrixRenderer: React.FC<DyadMatrixRendererProps> = ({
  block,
  activeFilters,
  countryFilters,
  dyadBlockMap,
}) => {
  const hasStatusFilters = activeFilters && activeFilters.size > 0;
  const hasCountryFilters = countryFilters && countryFilters.size > 0;

  // Filter dyads by status and/or country
  const visibleDyads = useMemo(() => {
    return block.dyads.filter((d) => {
      if (hasStatusFilters) {
        const label =
          DYAD_STATUS_PALETTE[d.statusScore]?.label ?? d.statusLabel;
        if (!activeFilters.has(label)) return false;
      }
      if (hasCountryFilters) {
        if (!countryFilters.has(d.countryA) && !countryFilters.has(d.countryB))
          return false;
      }
      return true;
    });
  }, [
    hasStatusFilters,
    hasCountryFilters,
    activeFilters,
    countryFilters,
    block.dyads,
  ]);

  const handleCardClick = useCallback(
    (entry: DyadMatrixEntry) => {
      if (!dyadBlockMap) return;

      const { bg } = statusStyle(entry.statusScore);
      const variants = dyadTitleVariants(entry.countryA, entry.countryB);
      for (const variant of variants) {
        const uid = dyadBlockMap.get(variant);
        if (uid) {
          const el = document.getElementById(`block-${uid}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            el.style.setProperty("--pulse-color", bg);
            el.classList.remove(styles.pulse);
            void el.offsetWidth;
            el.classList.add(styles.pulse);
            const onEnd = () => {
              el.classList.remove(styles.pulse);
              el.style.removeProperty("--pulse-color");
              el.removeEventListener("animationend", onEnd);
            };
            el.addEventListener("animationend", onEnd);
            return;
          }
        }
      }
    },
    [dyadBlockMap],
  );

  const clickable = !!dyadBlockMap;

  return (
    <div id={`block-${block.uid}`} className={styles.wrapper}>
      <div className={styles.strip}>
        {visibleDyads.map((entry, i) => {
          const { bg, text } = statusStyle(entry.statusScore);
          const label =
            DYAD_STATUS_PALETTE[entry.statusScore]?.label ?? entry.statusLabel;

          return (
            <div
              key={`${entry.countryA}-${entry.countryB}-${i}`}
              className={`${styles.card}${clickable ? ` ${styles.cardClickable}` : ""}`}
              data-dyad-id={entry.dyadId}
              data-assessment-id={entry.assessmentId}
              onClick={clickable ? () => handleCardClick(entry) : undefined}
            >
              <div className={styles.cardHeader}>
                <CountryLabel
                  name={entry.countryA}
                  countryId={entry.countryAId}
                />
                <CountryLabel
                  name={entry.countryB}
                  countryId={entry.countryBId}
                />
              </div>
              <div className={styles.cardStatus}>
                <span
                  className={styles.statusBadge}
                  style={{ backgroundColor: bg, color: text }}
                >
                  {label}
                </span>
              </div>
              <div className={styles.cardHeadline}>{entry.headline}</div>
            </div>
          );
        })}
        {visibleDyads.length === 0 && (
          <p className={styles.emptyMessage}>
            No dyads match the selected filter.
          </p>
        )}
      </div>
    </div>
  );
};

export default DyadMatrixRenderer;
